<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import TgModal from "@/abc-dsk/components/Modal/TgModal.vue";
import { Size } from "@/abc-dsk/options";
import { useArticleFont } from "@/common/composables/use-article-font.composable";
import { translate } from "@/common/gyldendal-helpers";
import IconWhiteDocumentNoBorder from "@/components/icons/white/IconWhiteDocumentNoBorder.vue";
import { useInflectionSchemeStore } from "@/stores";
import type { GyldendalInflectionSchemeData } from "@/types";

import InflectionSchemePronunciations from "./InflectionSchemePronunciations.vue";
import InflectionSchemePronunciationVariants from "./InflectionSchemePronunciationVariants.vue";

const { t } = useI18n();
const inflectionSchemeStore = useInflectionSchemeStore();

const { articleFontClass } = useArticleFont();
const inflectionSchemeData = computed<GyldendalInflectionSchemeData | null>(
  () => inflectionSchemeStore.inflectionSchemeData as GyldendalInflectionSchemeData | null
);
const hasDescriptionItems = computed(() => {
  if (inflectionSchemeData.value === null) {
    return false;
  }

  return (
    inflectionSchemeData.value.shortAbbreviations.length > 0 ||
    inflectionSchemeData.value.longAbbreviations.length > 0 ||
    inflectionSchemeData.value.abbreviates.length > 0 ||
    inflectionSchemeData.value.variants.length > 0
  );
});

const inflectionTable = computed(() => {
  if (inflectionSchemeData.value === null) {
    return "";
  }

  return inflectionSchemeData.value.inflectionTable ?? "";
});

const inflectionFormsTranslated = computed(
  () =>
    inflectionSchemeData.value?.inflectionForms.map((inflectionForm: string) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      translate(inflectionForm, inflectionSchemeData.value!.language)
    ) ?? []
);

const wordclassTranslated = computed(() => {
  if (!inflectionSchemeData.value) {
    return null;
  }

  return translate(inflectionSchemeData.value.wordclass, inflectionSchemeData.value.language);
});

const gendersTranslated = computed(
  () =>
    inflectionSchemeData.value?.genders.map((gender: string) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      translate(gender, inflectionSchemeData.value!.language)
    ) ?? []
);

const typesTranslated = computed(
  () =>
    inflectionSchemeData.value?.types.map((type: string) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      translate(type, inflectionSchemeData.value!.language)
    ) ?? []
);

const onClose = () => {
  inflectionSchemeStore.closeInflectionScheme();
};
</script>

<template>
  <TgModal
    :is-open="inflectionSchemeStore.isInflectionSchemeOpen"
    dialog-class="inflection-scheme-modal"
    :content-class="articleFontClass"
    body-class="inflection-scheme-modal__body"
    :size="Size.Xl"
    @close="onClose()"
  >
    <template v-if="inflectionSchemeData" #modal-header>
      <span>
        <span class="inflection-scheme-modal__header-icon">
          <IconWhiteDocumentNoBorder class="text-primary" role="presentation" />
        </span>
        <span class="inflection-scheme-modal__header-text">
          {{ inflectionSchemeData.lemma }}
        </span>
        <i class="inflection-scheme-modal__header-word-information">
          <span v-if="wordclassTranslated" class="mx-1">
            {{ wordclassTranslated }}
          </span>
          <span v-if="gendersTranslated.length > 0" class="mx-1">
            {{ gendersTranslated.join("/") }}
          </span>
          <span v-if="inflectionFormsTranslated.length > 0" class="mx-1">
            {{ inflectionFormsTranslated.reverse().join("") }}
          </span>
          <span v-if="typesTranslated.length > 0" class="mx-1">
            ({{ typesTranslated.join(", ") }})
          </span>

          <span v-if="inflectionSchemeData.description" class="mx-1">
            (<span>{{ inflectionSchemeData.description }}</span
            >)
          </span>

          <span v-if="inflectionSchemeData.pronunciations.length > 0" class="mx-1">
            <InflectionSchemePronunciations :items="inflectionSchemeData.pronunciations" />
          </span>

          <span v-if="inflectionSchemeData.pronunciationVariants.length > 0" class="mx-1">
            <span class="inflection-scheme-modal__pronunciation-label">
              {{ t("article.pronunciation.label") }}:
            </span>
            <InflectionSchemePronunciationVariants
              :items="inflectionSchemeData.pronunciationVariants"
            />
          </span>

          <span v-if="inflectionSchemeData.usage" class="mx-1">
            {{ inflectionSchemeData.usage }}
          </span>
        </i>
      </span>
    </template>

    <div v-if="inflectionSchemeData && hasDescriptionItems" class="inflection-scheme-description">
      <div
        v-if="inflectionSchemeData.shortAbbreviations.length > 0"
        class="d-flex justify-content-start"
      >
        <div class="inflection-scheme-description__listing-header">
          {{ t("article.inflectionScheme.abbreviationFor") }}
        </div>
        <div
          v-for="(abbreviationFor, index) in inflectionSchemeData.shortAbbreviations"
          :key="index"
          class="inflection-scheme-description__listing-item"
        >
          <span v-if="index < inflectionSchemeData.shortAbbreviations.length - 1"
            >{{ abbreviationFor }},
          </span>
          <span v-else>{{ abbreviationFor }}</span>
        </div>
      </div>
      <div
        v-if="inflectionSchemeData.longAbbreviations.length > 0"
        class="d-flex justify-content-start"
      >
        <div class="inflection-scheme-description__listing-header">
          {{ t("article.inflectionScheme.abbreviationFor") }}
        </div>
        <div
          v-for="(abbreviationForBlind, index) in inflectionSchemeData.longAbbreviations"
          :key="index"
          class="inflection-scheme-description__listing-item"
        >
          <span v-if="index < inflectionSchemeData.longAbbreviations.length - 1"
            >{{ abbreviationForBlind }},
          </span>
          <span v-else>{{ abbreviationForBlind }}</span>
        </div>
      </div>
      <div v-if="inflectionSchemeData.abbreviates.length > 0" class="d-flex justify-content-start">
        <div class="inflection-scheme-description__listing-header">
          {{ t("article.inflectionScheme.abbreviated") }}
        </div>
        <div
          v-for="(abbreviatedLemmaRef, index) in inflectionSchemeData.abbreviates"
          :key="index"
          class="inflection-scheme-description__listing-item"
        >
          <span v-if="index < inflectionSchemeData.abbreviates.length - 1"
            >{{ abbreviatedLemmaRef }},
          </span>
          <span v-else>{{ abbreviatedLemmaRef }}</span>
        </div>
      </div>

      <div v-if="inflectionSchemeData.variants.length > 0" class="d-flex justify-content-start">
        <div class="inflection-scheme-description__listing-header">
          {{ t("article.inflectionScheme.variant") }}
        </div>
        <div
          v-for="(variants, index) in inflectionSchemeData.variants"
          :key="index"
          class="inflection-scheme-description__listing-item"
        >
          <span v-if="index < inflectionSchemeData.variants.length - 1">{{ variants }}, </span>
          <span v-else>{{ variants }}</span>
        </div>
      </div>
    </div>

    <div v-if="inflectionTable" class="inflection-scheme__table" v-html="inflectionTable"></div>
  </TgModal>
</template>
